import React, { useState, useRef, useEffect } from 'react';
import { User as UserIcon, Shield, Coins, ArrowLeft, LogOut, PlusCircle, BookOpen, Menu, X } from 'lucide-react';
import { useAuth } from '../AuthProvider';
import { Button } from './ui/Button';
import { VipBadge, formatVipExpireDate } from './ui/VipBadge';
import { useGeneralSettings } from '../hooks/useQueries';

const DEFAULT_LOGO_URL = "https://nhagiaoso.com/media/thumbnail/thumb_1785376576637_332682501.png";

interface HeaderNavbarProps {
  activeTab: 'profile' | 'users' | 'wallet' | 'questions';
  setActiveTab: (tab: 'profile' | 'users' | 'wallet' | 'questions') => void;
  returnUrl?: string;
  sourceAppName?: string;
  onBack?: () => void;
  onOpenTopUp?: () => void;
}

export const HeaderNavbar: React.FC<HeaderNavbarProps> = ({
  activeTab,
  setActiveTab,
  returnUrl,
  sourceAppName,
  onBack,
  onOpenTopUp,
}) => {
  const { userProfile: authProfile, loading, logout } = useAuth();
  const { data: generalSettingsData } = useGeneralSettings();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Đọc logo từ React Query cache (home_configs -> general_settings)
  const logoUrl = generalSettingsData?.data?.header?.url || 
                  generalSettingsData?.header?.url || 
                  generalSettingsData?.url || 
                  DEFAULT_LOGO_URL;

  const userProfile = authProfile;

  // Nguồn dữ liệu & đọc trường Xu chuẩn
  const userCredits = typeof userProfile?.credits === 'number' ? userProfile.credits : 0;
  const vipTierNum = typeof userProfile?.vipTier === 'number' ? userProfile.vipTier : (userProfile?.vipTier ? Number(userProfile.vipTier) : 0);

  const handleXuBadgeClick = () => {
    if (onOpenTopUp) {
      onOpenTopUp();
    } else {
      setActiveTab('wallet');
    }
  };

  const renderTabs = (isMobile: boolean = false) => {
    const isAdmin = userProfile?.role === 'admin';
    const containerClasses = isMobile 
      ? "flex flex-col space-y-1 w-full"
      : "hidden md:flex items-center space-x-1 bg-slate-100 p-1 rounded-xl";
    const btnClasses = isMobile
      ? "px-4 py-3 rounded-lg text-sm font-bold transition-all flex items-center w-full"
      : "px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center";

    return (
      <div className={containerClasses}>
        <button
          type="button"
          onClick={() => { setActiveTab('profile'); setIsMobileMenuOpen(false); }}
          className={`${btnClasses} ${
            activeTab === 'profile'
              ? 'bg-white text-[#236539] shadow-2xs'
              : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
          }`}
        >
          <UserIcon className="w-4 h-4 mr-2 md:w-3.5 md:h-3.5 md:mr-1" />
          <span>Hồ sơ</span>
        </button>
        <button
          type="button"
          onClick={() => { setActiveTab('wallet'); setIsMobileMenuOpen(false); }}
          className={`${btnClasses} ${
            activeTab === 'wallet'
              ? 'bg-white text-amber-800 shadow-2xs'
              : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
          }`}
        >
          <Coins className="w-4 h-4 mr-2 md:w-3.5 md:h-3.5 md:mr-1 text-amber-600" />
          <span>Ví Xu - VIP</span>
        </button>
        <button
          type="button"
          onClick={() => { setActiveTab('questions'); setIsMobileMenuOpen(false); }}
          className={`${btnClasses} ${
            activeTab === 'questions'
              ? 'bg-white text-[#236539] shadow-2xs'
              : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
          }`}
        >
          <BookOpen className="w-4 h-4 mr-2 md:w-3.5 md:h-3.5 md:mr-1 text-[#236539]" />
          <span>NH Câu hỏi</span>
        </button>
        {isAdmin && (
          <button
            type="button"
            onClick={() => { setActiveTab('users'); setIsMobileMenuOpen(false); }}
            className={`${btnClasses} ${
              activeTab === 'users'
                ? 'bg-white text-[#224783] shadow-2xs'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            <Shield className="w-4 h-4 mr-2 md:w-3.5 md:h-3.5 md:mr-1 text-[#224783]" />
            <span>Quản trị</span>
          </button>
        )}
        {isMobile && (
          <>
            <div className="h-px bg-slate-100 my-1 mx-2" />
            <button
              type="button"
              onClick={() => { logout(); setIsMobileMenuOpen(false); }}
              className="px-4 py-3 rounded-lg text-sm font-bold transition-all flex items-center w-full text-rose-600 hover:bg-rose-50"
            >
              <LogOut className="w-4 h-4 mr-2" />
              <span>Đăng xuất</span>
            </button>
          </>
        )}
      </div>
    );
  };

  const rootDomain = typeof window !== 'undefined'
    ? window.location.hostname.split('.').slice(-2).join('.')
    : 'nhagiaoso.com';
  const mainPortalUrl = `https://${rootDomain}/`;

  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-200/80 shadow-xs relative">
      <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
        {/* Left section: Return app or Brand Title */}
        <div className="flex items-center space-x-3 shrink-0">
          {returnUrl ? (
            <Button
              variant="secondary"
              className="pl-3 pr-4 shadow-2xs border border-slate-200 text-xs md:text-sm whitespace-nowrap"
              onClick={onBack}
            >
              <ArrowLeft className="w-4 h-4 mr-1.5" />
              <span className="hidden sm:inline">Quay lại {sourceAppName || 'Ứng dụng'}</span>
              <span className="sm:hidden">Quay lại</span>
            </Button>
          ) : (
            <a
              href={mainPortalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center hover:opacity-90 transition-opacity"
              title="Về trang chủ"
            >
              <img
                src={logoUrl}
                alt="Nhà Giáo Số"
                className="h-9 md:h-10 w-auto object-contain"
                referrerPolicy="no-referrer"
              />
            </a>
          )}
        </div>

        {/* Center/Right section: Tabs (Desktop) -> Xu + VIP Badge -> Logout & Mobile Menu */}
        <div className="flex items-center space-x-2 md:space-x-3">
          
          {/* Navigation Tabs (Desktop only) */}
          {renderTabs(false)}

          {/* HUY HIỆU XU (XU BADGE) KẾT HỢP BADGE VIP/SVIP */}
          <div
            onClick={handleXuBadgeClick}
            title={vipTierNum > 0 ? `Ví Xu & Hạng VIP (Hạn: ${formatVipExpireDate(userProfile?.vipExpireAt)})` : "Bấm để xem Ví Xu hoặc Nạp thêm Xu / Nâng cấp VIP"}
            className="group cursor-pointer flex items-center space-x-1.5 md:space-x-2 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200/90 hover:border-amber-400 px-2.5 md:px-3 py-1.5 rounded-full shadow-2xs hover:shadow-xs transition-all active:scale-95"
          >
            <div className="w-5 h-5 md:w-6 md:h-6 rounded-full bg-amber-400 text-amber-950 flex items-center justify-center font-bold text-xs shadow-xs group-hover:rotate-12 transition-transform shrink-0">
              <Coins className="w-3 md:w-3.5 h-3 md:h-3.5 text-amber-950" />
            </div>

            {loading ? (
              <div className="w-10 md:w-14 h-3 md:h-4 bg-amber-200/60 animate-pulse rounded-md" />
            ) : (
              <div className="flex items-center space-x-1">
                <span className="text-[13px] md:text-sm font-black text-amber-900 group-hover:text-amber-950">
                  {userCredits.toLocaleString('vi-VN')}
                </span>
                <span className="text-[11px] font-bold text-amber-700 hidden md:inline">Xu</span>
              </div>
            )}

            {/* Thay thế icon + thành badge VIP / SVIP nếu có hoặc nút + nếu là user */}
            {vipTierNum > 0 ? (
              <VipBadge
                vipTier={userProfile?.vipTier}
                vipExpireAt={userProfile?.vipExpireAt}
                size="xs"
                className="ml-0.5 scale-90 md:scale-100 transform origin-left"
              />
            ) : (
              <PlusCircle className="w-3.5 h-3.5 text-amber-600 group-hover:text-amber-800 transition-colors ml-0.5 shrink-0" />
            )}
          </div>

          <div className="flex items-center space-x-1" ref={menuRef}>
            {/* Mobile Menu Toggle Button */}
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-1.5 md:hidden text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition-colors border border-transparent hover:border-slate-200"
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>

            {/* Mobile Dropdown Menu */}
            {isMobileMenuOpen && (
              <div className="absolute top-[60px] right-4 w-48 bg-white rounded-xl shadow-xl border border-slate-200 p-2 md:hidden animate-in fade-in zoom-in-95 duration-200">
                {renderTabs(true)}
              </div>
            )}

            {/* Logout Button (Desktop) */}
            <button
              type="button"
              onClick={logout}
              title="Đăng xuất"
              className="hidden md:flex p-2 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-colors border border-transparent hover:border-rose-100"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default HeaderNavbar;

